import {
    calculateConvertedScore,
    calculateConvertedScoreByTime,
    calculateEvaluationScore,
    calculateEvaluationScoreUpdateAndCreateByTime,
    calculateNumberToDay,
} from "./Compare";
import {getDataByMonth, getRecentDataByMonth} from "../../../../api/evaluation/SlaService";
import {getPreviousMonthKey} from "./slaCompareMonthUtils";

export const DEFAULT_SLA_ITEMS = [
    {title: "장애건수", weight: 0, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "ERROR-COUNT"},
    {title: "장애복구율", weight: 0, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "ERROR-RECOVERY"},
    {title: "동일 장애 발생건수 (건당)", weight: -2, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "ERROR-REPEAT-COUNT"},
    {title: "적기보고 실시율", weight: 0, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "REPORT"},
    {title: "산출물 납기준수율", weight: 0, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "OUTPUT"},
    {title: "서비스 요청 적기 처리율", weight: 0, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "SERVICE"},
    {title: "작업 요청 적기 처리율", weight: 0, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "WORK"},
    {title: "점검 요청 적기 처리율", weight: 0, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "INSPECTION"},
    {title: "성능개선 제안(건당)", weight: 2, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "IMPROVEMENT"},
    {title: "내부자료 유출 및 중요 데이터 손실(건당)", weight: -2, countOne: 0, countTwo: 0, timeOne: 0, timeTwo: 0, goal: 98, min: 95, section: "LEAK"},
];

const EXCLUDED_WEIGHT_SECTIONS = ["ERROR-REPEAT-COUNT", "OUTPUT", "IMPROVEMENT", "LEAK"];

const CONFIG_FIELDS = ["weight", "goal", "min"];

function hasSavedItemConfig(items) {
    if (!Array.isArray(items) || items.length === 0) {
        return false;
    }

    return items.some((row) => {
        const weight = parseFloat(row.weight);
        return !Number.isNaN(weight) && weight !== 0;
    });
}

function pickConfigFields(source, fields = CONFIG_FIELDS) {
    if (!source) {
        return {};
    }

    return fields.reduce((acc, field) => {
        if (source[field] !== undefined && source[field] !== null && source[field] !== "") {
            acc[field] = source[field];
        }
        return acc;
    }, {});
}

async function fetchPreviousMonthTemplate(month, maxLookback = 12) {
    let cursor = getPreviousMonthKey(month);

    for (let i = 0; i < maxLookback && cursor; i++) {
        const response = await getDataByMonth(cursor);
        if (response?.status === 200 && response.data && hasSavedItemConfig(response.data.item)) {
            return {
                item: response.data.item,
                availability: response.data.availability ?? response.data.children ?? [],
            };
        }
        cursor = getPreviousMonthKey(cursor);
    }

    return null;
}

function mergeItems(sourceItems, isCreate) {
    return DEFAULT_SLA_ITEMS.map((originalItem) => {
        const matchedResponse = sourceItems?.find((res) => res.section === originalItem.section);

        if (!matchedResponse) {
            return originalItem;
        }

        const config = pickConfigFields(matchedResponse);

        if (isCreate) {
            return {
                ...originalItem,
                ...config,
                countOne: 0,
                countTwo: 0,
                timeOne: 0,
                timeTwo: 0,
                timeOneKr: calculateNumberToDay(0),
                timeTwoKr: calculateNumberToDay(0),
            };
        }

        return {
            ...originalItem,
            ...matchedResponse,
            ...config,
            timeOneKr: calculateNumberToDay(matchedResponse.timeOne),
            timeTwoKr: calculateNumberToDay(matchedResponse.timeTwo),
            min: matchedResponse.min ? matchedResponse.min : 95,
            goal: matchedResponse.goal ? matchedResponse.goal : 98,
        };
    });
}

function buildChildren(availabilityRows, lastDayOfMonth, isCreate) {
    return (availabilityRows ?? []).map((row) => {
        const operationTimeToInt = lastDayOfMonth * (row.totalCount ? row.totalCount : 1);
        const stopTimeToInt = isCreate ? 0 : (row.stopTimeToInt ?? 0);

        return {
            ...row,
            weight: row.weight ? row.weight : 10,
            operationTimeToInt,
            operationTimeKr: `${calculateNumberToDay(operationTimeToInt)}(${row.totalCount ?? 0} 건)`,
            stopTimeToInt,
            stopTimeKr: calculateNumberToDay(stopTimeToInt),
            min: row.min ? row.min : 95,
            goal: row.goal ? row.goal : 98,
            checked: row.checked ?? false,
        };
    });
}

function buildEditorData(sourceData, lastDayOfMonth, isCreate) {
    const templateItems = hasSavedItemConfig(sourceData?.item) ? sourceData.item : undefined;

    return {
        items: mergeItems(templateItems, isCreate),
        children: buildChildren(sourceData?.availability, lastDayOfMonth, isCreate),
    };
}

export function computeWeightSum(children, items) {
    const childrenTotal = children.reduce((acc, curr) => {
        const weight = parseFloat(curr.weight) || 0;
        return curr.checked ? acc + weight : acc;
    }, 0);

    const itemTotal = items.reduce((acc, curr) => {
        const weight = parseFloat(curr.weight) || 0;
        if (!EXCLUDED_WEIGHT_SECTIONS.includes(curr.section)) {
            return acc + weight;
        }
        return acc;
    }, 0);

    return childrenTotal + itemTotal;
}

export function handleSlaInputChange(fnc, value, index, target, type) {
    fnc((prevData) =>
        prevData.map((dataItem, dataIndex) => {
            if (dataIndex !== index) {
                return dataItem;
            }

            const updatedItem = {...dataItem, [target]: value};

            if (type === "children") {
                const evaluationScore = calculateEvaluationScoreUpdateAndCreateByTime(updatedItem);
                const convertedScore = calculateConvertedScoreByTime(evaluationScore, updatedItem.weight);
                return {...updatedItem, evaluationScore, convertedScore};
            }

            if (type === "item") {
                const evaluationScore = calculateEvaluationScore(updatedItem);
                const convertedScore = calculateConvertedScore(evaluationScore, updatedItem);
                return {...updatedItem, evaluationScore, convertedScore};
            }

            return updatedItem;
        }),
    );
}

export function applyWeightCalculations(children, items) {
    const nextChildren = children.map((child) => {
        const evaluationScore = calculateEvaluationScoreUpdateAndCreateByTime(child);
        const convertedScore = calculateConvertedScoreByTime(evaluationScore, child.weight);
        return {
            ...child,
            weight: child.weight === "" ? 0 : child.weight,
            evaluationScore,
            convertedScore,
        };
    });

    const nextItems = items.map((eachItem) => {
        const evaluationScore = calculateEvaluationScore(eachItem);
        const convertedScore = calculateConvertedScore(evaluationScore, eachItem);
        return {
            ...eachItem,
            weight: eachItem.weight === "" ? 0 : eachItem.weight,
            evaluationScore,
            convertedScore,
        };
    });

    return {children: nextChildren, items: nextItems};
}

export function computeEvaluationResult(children, items) {
    const childrenTotal = children.reduce((acc, curr) => {
        const convertedScore = parseFloat(curr.convertedScore) || 0;
        return curr.checked ? acc + convertedScore : acc;
    }, 0);

    const itemTotal = items.reduce((acc, curr) => {
        const convertedScore = parseFloat(curr.convertedScore) || 0;
        return acc + convertedScore;
    }, 0);

    return (childrenTotal + itemTotal).toFixed(2);
}

export function buildSavePayload(month, children, items, evaluationResult) {
    return {
        month,
        children: children
            .filter((child) => child.checked)
            .map((child) => ({
                ...child,
                code: {id: child.id, name: child.name},
                id: null,
            })),
        item: items.map((row) => ({...row, id: null})),
        evaluationResult: parseFloat(evaluationResult),
    };
}

export async function loadSlaEditorData(month, mode) {
    const [y, m] = month.split("-").map(Number);
    const lastDayOfMonth = new Date(y, m, 0).getDate() * 24 * 60;

    if (mode === "update") {
        const response = await getDataByMonth(month);
        return buildEditorData(response.data ?? {}, lastDayOfMonth, false);
    }

    const recentResponse = await getRecentDataByMonth(month);
    const recentData = recentResponse?.data ?? {};
    const previousTemplate = await fetchPreviousMonthTemplate(month);

    const sourceData = {
        item: previousTemplate?.item
            ?? (hasSavedItemConfig(recentData.item) ? recentData.item : undefined),
        availability: recentData.availability ?? recentData.children ?? previousTemplate?.availability ?? [],
    };

    return buildEditorData(sourceData, lastDayOfMonth, true);
}
